import { useState } from "react";
import { Label } from "@groot/ui/label";
import { RequestStatusBadge } from "@/components/status-badges";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@groot/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@groot/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@groot/ui/dialog";
import { toast } from "sonner";
import {
  useMomentRequests,
  useCreateMomentRequest,
  useUpdateMomentRequest,
  useDeleteMomentRequest,
} from "@/pages/moment-request/hooks/useMomentRequests";
import { useCustomers } from "@/pages/customer/hooks/useCustomers";
import { PageLayout } from "@groot/shell/components/layout/PageLayout";
import { Input } from "@groot/ui/input";
import { Link } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@groot/ui/table";
import { SkeletonTable } from "@groot/ui/loading-skeleton";
import { EmptyState } from "@groot/ui/empty-state";
import { RowActions, RowActionItem, RowActionSeparator } from "@/components/row-actions";
import { Search, Video, Plus, Trash2, Eye, Copy, CheckCircle } from "lucide-react";

const momentRequestSchema = z.object({
  customerId: z.number().int().positive("Customer is required"),
});

type MomentRequestFormValues = z.infer<typeof momentRequestSchema>;

export function MomentRequests() {
  const { data: momentRequests, isLoading } = useMomentRequests();
  const { data: customers } = useCustomers();
  const createMomentRequest = useCreateMomentRequest();
  const updateMomentRequest = useUpdateMomentRequest();
  const deleteMomentRequest = useDeleteMomentRequest();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const form = useForm<MomentRequestFormValues>({
    resolver: zodResolver(momentRequestSchema),
    defaultValues: {
      customerId: 0,
    },
  });

  const filteredRequests = momentRequests?.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return r.customer?.name.toLowerCase().includes(q) || r.customer?.mobileNumber?.includes(q);
  });

  const onSubmit = async (values: MomentRequestFormValues) => {
    try {
      await createMomentRequest.mutateAsync(values);
      toast.success("Success", { description: "Moment request created" });
      form.reset();
      setOpen(false);
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Unable to create moment request" });
    }
  };

  const completeRequest = async (id: number) => {
    try {
      await updateMomentRequest.mutateAsync({ id, data: { status: "completed" } });
      toast.success("Success", { description: "Moment request completed" });
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Unable to complete moment request" });
    }
  };

  const removeRequest = async (id: number) => {
    try {
      await deleteMomentRequest.mutateAsync(id);
      toast.success("Deleted", { description: "Moment request removed" });
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Unable to delete moment request" });
    }
  };

  const copyPublicLink = (link: string) => {
    navigator.clipboard.writeText(`${window.location.origin}/public/moment-requests/${link}`);
    toast.success("Copied", { description: "Upload link copied to clipboard" });
  };

  const copyViewLink = (link: string) => {
    navigator.clipboard.writeText(`${window.location.origin}/public/moment-requests/view/${link}`);
    toast.success("Copied", { description: "View link copied to clipboard" });
  };

  const actions = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="size-4" />
          Create Request
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Moment Request</DialogTitle>
        </DialogHeader>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
          <div className="space-y-2">
            <Label>Customer</Label>
            <Select
              onValueChange={(value) => {
                form.setValue("customerId", parseInt(value));
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select customer" />
              </SelectTrigger>
              <SelectContent>
                {customers?.map((customer) => (
                  <SelectItem key={customer.id} value={customer.id.toString()}>
                    {customer.name} {customer.mobileNumber ? `— ${customer.mobileNumber}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.formState.errors.customerId && (
              <p className="text-sm text-destructive">{form.formState.errors.customerId.message}</p>
            )}
          </div>
          <Button className="w-full" type="submit" disabled={createMomentRequest.isPending}>
            {createMomentRequest.isPending ? "Creating..." : "Create Request"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );

  const rowActions = (request: NonNullable<typeof filteredRequests>[number]) => (
    <RowActions>
      <RowActionItem asChild>
        <Link to={`/moment-requests/${request.id}`}>
          <Eye className="size-4" />
          View
        </Link>
      </RowActionItem>
      <RowActionItem onClick={() => copyPublicLink(request.publicLink)}>
        <Copy className="size-4" />
        Copy Upload Link
      </RowActionItem>
      <RowActionItem onClick={() => copyViewLink(request.viewLink)}>
        <Copy className="size-4" />
        Copy View Link
      </RowActionItem>
      {request.status === "pending" && (
        <RowActionItem
          onClick={() => completeRequest(request.id)}
          disabled={updateMomentRequest.isPending}
        >
          <CheckCircle className="size-4" />
          Complete
        </RowActionItem>
      )}
      <RowActionSeparator />
      <RowActionItem
        destructive
        onClick={() => removeRequest(request.id)}
        disabled={deleteMomentRequest.isPending}
      >
        <Trash2 className="size-4" />
        Delete
      </RowActionItem>
    </RowActions>
  );

  return (
    <PageLayout title="Moments" description="Manage video moment requests" actions={actions}>
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            placeholder="Search by customer name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {isLoading && (
          <div className="overflow-hidden rounded-lg border">
            <SkeletonTable columns={5} rows={6} />
          </div>
        )}

        {!isLoading && filteredRequests && filteredRequests.length > 0 && (
          <div className="border border-border rounded-lg overflow-hidden">
            <ul className="md:hidden divide-y divide-border">
              {filteredRequests.map((request) => (
                <li key={request.id} className="flex items-center gap-2 py-3 pl-4 pr-2">
                  <Link to={`/moment-requests/${request.id}`} className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-foreground">
                      {request.customer?.name}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span>#{request.id}</span>
                      <RequestStatusBadge status={request.status} />
                      <span>{request._count?.videos ?? 0} videos</span>
                    </div>
                  </Link>
                  {rowActions(request)}
                </li>
              ))}
            </ul>

            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="hidden sm:table-cell">ID</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden sm:table-cell">Videos</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRequests.map((request) => (
                    <TableRow
                      key={request.id}
                      className="cursor-pointer hover:bg-muted/50 border-border"
                    >
                      <TableCell className="text-sm text-foreground hidden sm:table-cell">
                        <Link
                          to={`/moment-requests/${request.id}`}
                          className="font-medium hover:text-primary"
                        >
                          #{request.id}
                        </Link>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {request.customer?.name}
                      </TableCell>
                      <TableCell>
                        <RequestStatusBadge status={request.status} />
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground hidden sm:table-cell">
                        {request._count?.videos ?? 0} uploaded
                      </TableCell>
                      <TableCell>{rowActions(request)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {!isLoading && filteredRequests?.length === 0 && (
          <EmptyState
            icon={Video}
            title={search ? "No requests found" : "No moment requests yet"}
            description={
              search
                ? "Try adjusting your search terms"
                : "Create a request to collect videos from customers"
            }
            action={
              !search && (
                <Button onClick={() => setOpen(true)}>
                  <Plus className="size-4" />
                  Create Request
                </Button>
              )
            }
          />
        )}
      </div>
    </PageLayout>
  );
}
